import React from "react";
import { Box, CircularProgress, Fade, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import { useTranslation } from "react-i18next";
import { useSound } from "../../../../../context/juno/SoundContext";
import { useJunoModels } from "../../../../../hooks/useJunoModels";
import ScrollableBox from "../../../../../components/common/ScrollableBox";
import {
  AudioUploadControl,
  DurationControl,
  GuidanceScaleControl,
  LyricControl,
  NegativePromptControl,
  PublicSettingControl,
  StepControl,
} from "./params";
import { ColumnBox } from "../../../../../utils/styledBox";

interface SoundParamsProps {
  generationCompleted: boolean;
  setGenerationCompleted: (completed: boolean) => void;
}

const SoundParams: React.FC<SoundParamsProps> = () => {
  const { t } = useTranslation();
  const { aiEngine } = useSound();
  const { getSoundModelParams } = useJunoModels();
  const modelParams = aiEngine ? getSoundModelParams(aiEngine) : [];

  return (
    <Box
      sx={{
        // height: "100%",
        borderRadius: 1,
        backgroundColor: (theme) => alpha(theme.palette.background.custom1, 0.5),
        backdropFilter: "blur(10px)",
        display: "flex",
        height: "100%", // 親から高さを継承
        overflow: "hidden", // スクロールは内部 Grid で
        width: "100%",
      }}
    >
      <ScrollableBox
        sx={{ flexGrow: 1, width: "100%", minHeight: 50, alignContent: "flex-start", alignItems: "flex-start" }}
      >
        <Box display={"flex"} flexDirection={"row"} alignItems={"center"} sx={{ py: 2, px: 4 }}>
          <SettingsSuggestIcon sx={{ mr: 1 }} />
          <Typography variant={"subtitle1"} component={"p"}>
            {t("juno.sound.params.settings")}
          </Typography>
        </Box>

        {/* モデルパラメータの取得 */}
        {aiEngine ? (
          <Fade in={true} key={aiEngine} timeout={500}>
            <ColumnBox sx={{ gap: 2, px: 4 }}>
              {/* モデルパラメータに基づいて動的にコンポーネントを表示 */}
              {modelParams.includes("duration") && <DurationControl />}
              {modelParams.includes("guidanceScale") && <GuidanceScaleControl />}
              {modelParams.includes("step") && <StepControl />}
              {modelParams.includes("negativePrompt") && <NegativePromptControl />}
              {modelParams.includes("lyric") && <LyricControl />}
              {modelParams.includes("audioUpload") && <AudioUploadControl />}
              {modelParams.includes("public") && <PublicSettingControl />}
            </ColumnBox>
          </Fade>
        ) : (
          <Box
            sx={{
              justifyContent: "center",
              display: "flex",
              width: "100%",
              height: "100%",
              alignItems: "center",
            }}
          >
            <Box>
              <CircularProgress />
            </Box>
          </Box>
        )}
      </ScrollableBox>
    </Box>
  );
};

export default SoundParams;
